import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getTechnique, type TechniqueField, type SWOTExample } from "@/lib/techniquesData";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Download, Plus, X } from "lucide-react";

export default function TechniqueWorkspace() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const technique = id ? getTechnique(id) : undefined;

  const [data, setData] = useState<Record<string, string | string[]>>(() => {
    if (!technique) return {};
    const init: Record<string, string | string[]> = {};
    technique.fields.forEach((f) => {
      init[f.id] = f.type === "list" ? [] : "";
    });
    return init;
  });

  if (!technique) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Technique not found.{" "}
        <button onClick={() => navigate("/studio/techniques")} className="text-accent underline">
          Back to library
        </button>
      </div>
    );
  }

  const updateField = (fieldId: string, value: string | string[]) => {
    setData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleExport = () => {
    let text = `# ${technique.name}\n\n`;
    technique.fields.forEach((f) => {
      text += `## ${f.label}\n`;
      const val = data[f.id];
      if (Array.isArray(val)) {
        val.forEach((item) => (text += `- ${item}\n`));
      } else {
        text += `${val || "(empty)"}\n`;
      }
      text += "\n";
    });
    const blob = new Blob([text], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${technique.id}-analysis.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full">
      <header className="border-b border-border shrink-0 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/studio/techniques")}
              className="gap-1.5"
            >
              <ArrowLeft className="h-4 w-4" /> Techniques
            </Button>
            <div className="h-5 w-px bg-border" />
            <div>
              <h1 className="font-display font-semibold text-foreground">
                {technique.name}
              </h1>
              <p className="text-xs text-muted-foreground font-body">
                {technique.shortDesc}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5">
            <Download className="h-3.5 w-3.5" /> Export
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="bg-secondary/50 rounded-md p-4 text-sm text-secondary-foreground font-body">
            <p className="font-semibold mb-1">When to use:</p>
            <p className="text-muted-foreground">{technique.when}</p>
          </div>

          {technique.id === "swot" && <SwotDiagram />}

          {technique.examples && technique.examples.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
                Real Industry Examples
              </h2>
              {technique.examples.map((ex) => (
                <SwotExampleCard key={ex.company} example={ex} />
              ))}
            </div>
          )}

          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider pt-2">
            Your Analysis
          </h2>

          <div
            className={
              technique.fields.length === 4
                ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
                : "space-y-4"
            }
          >
            {technique.fields.map((field) => (
              <FieldEditor
                key={field.id}
                field={field}
                value={data[field.id]}
                onChange={(v) => updateField(field.id, v)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SwotDiagram() {
  const quadrants = [
    { label: "Strengths", axis: "Internal · Positive", bg: "bg-emerald-50 dark:bg-emerald-950/40", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-800 dark:text-emerald-300", items: ["Unique capabilities", "Strong brand / IP", "Skilled team", "Loyal customer base"] },
    { label: "Weaknesses", axis: "Internal · Negative", bg: "bg-rose-50 dark:bg-rose-950/40", border: "border-rose-300 dark:border-rose-700", text: "text-rose-800 dark:text-rose-300", items: ["Resource constraints", "Legacy systems", "Skill gaps", "High operating costs"] },
    { label: "Opportunities", axis: "External · Positive", bg: "bg-sky-50 dark:bg-sky-950/40", border: "border-sky-300 dark:border-sky-700", text: "text-sky-800 dark:text-sky-300", items: ["Emerging markets", "New technology", "Competitor gaps", "Regulatory tailwinds"] },
    { label: "Threats", axis: "External · Negative", bg: "bg-amber-50 dark:bg-amber-950/40", border: "border-amber-300 dark:border-amber-700", text: "text-amber-800 dark:text-amber-300", items: ["New competitors", "Regulatory changes", "Economic downturn", "Supply-chain risk"] },
  ];

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">Framework Diagram</h2>
      <div className="border border-border rounded-md overflow-hidden">
        {/* Axis labels */}
        <div className="grid grid-cols-2 border-b border-border">
          <div className="text-center py-1 text-[10px] font-mono text-muted-foreground border-r border-border bg-secondary/30">INTERNAL</div>
          <div className="text-center py-1 text-[10px] font-mono text-muted-foreground bg-secondary/30">EXTERNAL</div>
        </div>
        <div className="grid grid-cols-2">
          {quadrants.map((q, i) => (
            <div key={q.label} className={`${q.bg} ${q.border} border p-4 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b" : ""}`}>
              <div className={`text-xs font-mono uppercase tracking-wider font-bold mb-1 ${q.text}`}>{q.label}</div>
              <div className="text-[10px] text-muted-foreground mb-2">{q.axis}</div>
              <ul className="space-y-1">
                {q.items.map((item) => (
                  <li key={item} className="text-xs text-foreground/80 flex gap-1.5 items-start">
                    <span className={`${q.text} mt-0.5`}>▸</span>{item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SwotExampleCard({ example }: { example: SWOTExample }) {
  const sections = [
    { label: "Strengths", items: example.strengths, text: "text-emerald-700 dark:text-emerald-400" },
    { label: "Weaknesses", items: example.weaknesses, text: "text-rose-700 dark:text-rose-400" },
    { label: "Opportunities", items: example.opportunities, text: "text-sky-700 dark:text-sky-400" },
    { label: "Threats", items: example.threats, text: "text-amber-700 dark:text-amber-400" },
  ];

  return (
    <div className="border border-border rounded-md bg-card overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-baseline gap-2">
        <span className="font-display font-semibold text-foreground">{example.company}</span>
        <span className="text-xs text-muted-foreground font-body">— {example.industry}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border">
        {sections.map((s) => (
          <div key={s.label} className="bg-card p-4">
            <div className={`text-[10px] font-mono uppercase tracking-wider font-bold mb-2 ${s.text}`}>{s.label}</div>
            <ul className="space-y-1">
              {s.items.map((item) => (
                <li key={item} className="text-xs text-foreground/80 flex gap-1.5 items-start">
                  <span className={`${s.text} mt-0.5 shrink-0`}>▸</span>{item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldEditor({
  field,
  value,
  onChange,
}: {
  field: TechniqueField;
  value: string | string[];
  onChange: (v: string | string[]) => void;
}) {
  const [newItem, setNewItem] = useState("");

  if (field.type === "list") {
    const items = Array.isArray(value) ? value : [];
    const addItem = () => {
      if (!newItem.trim()) return;
      onChange([...items, newItem.trim()]);
      setNewItem("");
    };
    return (
      <div className="border border-border rounded-md p-4 bg-card">
        <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2 block">
          {field.label}
        </label>
        <div className="space-y-1.5 mb-3">
          {items.map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-2 text-sm font-body bg-secondary/50 rounded px-3 py-1.5"
            >
              <span className="flex-1">{item}</span>
              <button
                onClick={() => onChange(items.filter((_, idx) => idx !== i))}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder={field.placeholder}
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
            className="text-sm"
          />
          <Button variant="outline" size="sm" onClick={addItem}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-md p-4 bg-card">
      <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2 block">
        {field.label}
      </label>
      <Textarea
        placeholder={field.placeholder}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="text-sm font-body"
      />
    </div>
  );
}
